import { TVL_BREAKDOWN_COLORS } from '@/constants/charts'
import { ASSET_GROUP_COLORS, NETWORK_COLORS } from '@/constants/colors'
import type { BreakdownItem } from '@/types/analytics'
import { getProtocolDisplayName } from '@/utils/protocol'
import { useMemo } from 'react'
import type { LpTokensBalanceGroup } from '../../components/dashboard/useGroupedBalances/useGroupedPositions'

type BreakdownTab = 'asset' | 'protocol' | 'network'

interface AggregatedBreakdown {
  displayName: string
  value: number
}

export function useBreakdownItems(
  lpTokensBalance: LpTokensBalanceGroup,
  tab: BreakdownTab,
  total: number
): BreakdownItem[] {
  return useMemo(() => {
    if (total === 0) return []

    const aggregated: Record<string, AggregatedBreakdown> = {}

    if (tab === 'asset') {
      for (const { groupName, groupTotalBalance } of lpTokensBalance) {
        aggregated[groupName] = {
          displayName: groupName,
          value: (aggregated[groupName]?.value ?? 0) + groupTotalBalance,
        }
      }
    } else {
      for (const { balances } of lpTokensBalance) {
        for (const position of balances) {
          const key = tab === 'protocol' ? position.protocol.name : position.network.name
          aggregated[key] = {
            displayName: tab === 'protocol' ? getProtocolDisplayName(position.protocol) : key,
            value: (aggregated[key]?.value ?? 0) + position.balanceUsd,
          }
        }
      }
    }

    const entries = Object.entries(aggregated).sort(([, a], [, b]) => b.value - a.value)

    return entries.map(([name, item], index) => {
      let color: string | undefined

      if (tab === 'asset') {
        color = ASSET_GROUP_COLORS[name]
      } else if (tab === 'network') {
        color = NETWORK_COLORS[name as keyof typeof NETWORK_COLORS]
      }

      return {
        name,
        displayName: item.displayName,
        value: item.value,
        percentage: item.value / total,
        imageUrl: null,
        imageFallbackUrl: null,
        color: color ?? TVL_BREAKDOWN_COLORS[index % TVL_BREAKDOWN_COLORS.length],
      }
    })
  }, [lpTokensBalance, tab, total])
}
